import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getSelectedFacebookPredictedLtv } from '@features/Analytics/services/facebook-ltv';
import { useFeatures } from '@features/FeatureToggle';
import { getStripeProductId } from '@features/Stripe/api';
import type { TpStripeVariants } from 'src/constants/variants';
import { getDataFromSessionStorage } from 'src/utils';
import { useGetIpInfo } from 'src/utils/hooks/query/use-get-ip-info';

type ProductData = {
  pltv?: null | string;
  product_id: string;
  us_pltv?: null | string;
  us_product_id?: string;
};

type SelectedProduct = {
  productId: string | undefined;
  isUsProductIdValid: boolean;
  isUsFeatureEnabled: boolean;
  pltv: string;
  predictedLtv: string | undefined;
  usPltv: string;
};

export const useGetStripeProductId = ({
  // почему по дефолту true? разве нужно чтобы в большинстве случаев запрашивался
  enabled = true,
  paymentSystemRegion,
  variant,
}: {
  variant?: string;
  enabled?: boolean;
  paymentSystemRegion?: string;
} = {}) => {
  const { shortLinkPayFeature: isShortLinkPayFeatureEnabled } = useFeatures();

  const { data: ipInfo } = useGetIpInfo();

  const userCountryCode = ipInfo?.countryCode;

  const variantFromStore =
    variant ??
    getDataFromSessionStorage({
      store: 'authStore',
      key: 'variant',
    }) ??
    'test';

  const transformedVariant: TpStripeVariants = (() => {
    if (isShortLinkPayFeatureEnabled)
      return 'WP20FBShrt-DEV-1677' as TpStripeVariants;
    return variantFromStore as TpStripeVariants;
  })();

  const selectFn = useCallback(
    (data: ProductData): SelectedProduct => {
      const countryIsUS = userCountryCode === 'US';
      const usId = data?.us_product_id;
      const baseId = data?.product_id;

      const hasUsId = typeof usId === 'string' && usId.length > 1;
      const hasBaseId = typeof baseId === 'string' && baseId.length > 1;
      const isUsProductIdValid = countryIsUS && hasUsId;
      const isUsFeatureEnabled =
        paymentSystemRegion === 'YouthUS' ||
        (!paymentSystemRegion && countryIsUS && hasUsId);
      const predictedLtv = getSelectedFacebookPredictedLtv({
        isUsFeatureEnabled,
        pltv: data?.pltv,
        usPltv: data?.us_pltv,
      });

      const normalizedUsId = hasUsId ? usId : undefined;
      const normalizedBaseId = hasBaseId ? baseId : undefined;
      const commonProductData = {
        isUsFeatureEnabled,
        isUsProductIdValid,
        pltv: data?.pltv ?? '',
        predictedLtv,
        usPltv: data?.us_pltv ?? '',
      };

      if (paymentSystemRegion === 'YouthUS') {
        return {
          productId: normalizedUsId,
          ...commonProductData,
        };
      }
      if (paymentSystemRegion === 'Youth') {
        return {
          productId: normalizedBaseId,
          ...commonProductData,
        };
      }

      return {
        productId:
          countryIsUS && isUsProductIdValid ? normalizedUsId : normalizedBaseId,
        ...commonProductData,
      };
    },
    [paymentSystemRegion, userCountryCode],
  );

  const { data } = useQuery<ProductData, unknown, SelectedProduct>({
    queryKey: ['stripe-product-id', transformedVariant, userCountryCode],
    queryFn: () => getStripeProductId({ variant: transformedVariant }),
    enabled,
    gcTime: 20 * 60 * 1000, // 20 minutes
    staleTime: 20 * 60 * 1000, // Consider data fresh for 20 minutes
    retry: 0,
    select: selectFn,
    placeholderData: {
      pltv: '',
      product_id: '',
      us_pltv: '',
      us_product_id: '',
    },
  });

  return (
    data ?? {
      productId: undefined,
      isUsProductIdValid: false,
      isUsFeatureEnabled: false,
      pltv: '',
      predictedLtv: undefined,
      usPltv: '',
    }
  );
};
